// 관리자 AI 비서 — 자연어 명령을 adminAgent 콜러블로 보내 조회·변경 작업을 수행하는 채팅 페이지
import { useRef, useState, useEffect } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";

import { plainError } from "@/lib/plainError";
interface Turn { role: "user" | "assistant"; text: string }
interface PendingAction { id: string; description: string }

// 서버 함수 제한 시간(300초)보다 조금 길게 기다린다 — 기본 70초면 AI가 아직 일하는 중에 화면이 먼저 포기한다(4차 QA 79번)
const CALL_TIMEOUT_MS = 310_000;
// 이 오류들은 서버가 실행 전에 거절한 것이다. 그 밖의 실패(시간 초과·연결 끊김)는 실행됐는지 알 수 없다
const REJECTED_BEFORE_RUN = new Set(["failed-precondition", "invalid-argument", "not-found", "permission-denied", "unauthenticated"]);

const EXAMPLES = [
  "전화 끝자리 5678 누구야?",
  "해강중 미배송 몇 명이야?",
  "금사중 김다미 이번 달 배송됐어?",
  "개림중 홍길동 주소를 부산 부산진구 ○○로 12, 2층으로 바꿔줘",
];

export default function AgentChat() {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<PendingAction[]>([]);
  const [runId, setRunId] = useState("");
  const [skipped, setSkipped] = useState<Set<string>>(new Set()); // 확인 카드에서 뺀 줄(Q22·76 — 건수 상한 없이 줄마다 빼기)
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [turns, pending, busy]);

  const send = async (text?: string) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    const next = [...turns, { role: "user" as const, text: msg }];
    setTurns(next);
    setInput("");
    setPending([]);
    setRunId("");
    setSkipped(new Set());
    setBusy(true);
    try {
      const fn = httpsCallable(functions, "adminAgent", { timeout: CALL_TIMEOUT_MS });
      const r = await fn({ messages: next });
      const data = (r.data as any) || {};
      setTurns((t) => [...t, { role: "assistant", text: data.reply || "(응답 없음)" }]);
      setPending(data.pendingActions || []);
      setRunId(data.runId || "");
    } catch (e: any) {
      setTurns((t) => [...t, { role: "assistant", text: `❌ 오류: ${plainError(e)}` }]);
    } finally {
      setBusy(false);
    }
  };

  const chosen = pending.filter((p) => !skipped.has(p.id));
  const toggle = (id: string) =>
    setSkipped((cur) => {
      const n = new Set(cur);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const execute = async () => {
    if (!chosen.length || busy) return;
    const shown = chosen; // 체크된 줄만 — 서버는 저장된 확인 카드의 부분집합인지 다시 본다
    setBusy(true);
    try {
      const fn = httpsCallable(functions, "adminAgent", { timeout: CALL_TIMEOUT_MS });
      const r = await fn({ mode: "execute", runId, actionIds: shown.map((p) => p.id) });
      const data = (r.data as any) || {};
      const results = (data.results || []) as string[];
      const text = data.running
        ? "이 작업은 이미 실행 중입니다. 잠시 뒤 학생 목록을 새로고침해 바뀌었는지 확인하세요."
        : data.already
          ? `이미 실행한 작업입니다(다시 실행하지 않았습니다).\n${results.join("\n")}`
          : results.join("\n");
      setTurns((t) => [...t, { role: "assistant", text }]);
    } catch (e: any) {
      const code = String(e?.code || "").replace(/^functions\//, "");
      const text = REJECTED_BEFORE_RUN.has(code)
        ? `❌ 실행하지 않았습니다: ${plainError(e)}`
        // 응답이 끊겼다 — 서버는 이미 실행했을 수 있으니 다시 누르게 하지 않는다
        : `⚠️ 응답을 받지 못했습니다. 이미 실행됐을 수 있습니다. 학생 목록을 새로고침해 아래 작업이 반영됐는지 확인하세요.\n${shown.map((p) => `- ${p.description}`).join("\n")}`;
      setTurns((t) => [...t, { role: "assistant", text }]);
    } finally {
      // 어느 경우든 같은 확인 카드의 「실행」 버튼은 끈다 — 같은 작업이 필요하면 명령을 다시 보내 새 카드를 받는다
      setPending([]);
      setRunId("");
      setSkipped(new Set());
      setBusy(false);
    }
  };

  const cancel = () => {
    setPending([]);
    setRunId("");
    setSkipped(new Set());
    setTurns((t) => [...t, { role: "assistant", text: "작업을 취소했습니다." }]);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-bold">🤖 AI 비서</h1>
        <p className="text-sm text-slate-500 mt-1">
          자연어로 명령하세요. 조회는 바로 실행되고, 주소 변경·택배 전환·전출 처리는 확인 버튼을 거칩니다.
        </p>
        <p className="text-xs text-slate-500 mt-1">
          명령과 학생 이름·학교·주소는 AI 회사(Anthropic, 미국)로 보내 처리합니다. 전화번호는 뒤 4자리만 보내고, 보호자 이름·메모는 보내지 않습니다.{" "}
          <a className="underline" href="https://freemilkkok.com/privacy" target="_blank" rel="noreferrer">개인정보처리방침</a>
        </p>
      </div>

      <div className="card p-4 min-h-[300px] space-y-3">
        {turns.length === 0 && (
          <div className="text-sm text-slate-400 space-y-2">
            <div>예시 명령을 눌러보세요.</div>
            <div className="flex flex-wrap gap-2">
              {EXAMPLES.map((ex) => (
                <button key={ex} className="chip-neutral hover:bg-slate-200" onClick={() => send(ex)}>
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className={t.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div
              className={
                t.role === "user"
                  ? "bg-milk-500 text-white rounded-2xl rounded-br-sm px-4 py-2 max-w-[85%] whitespace-pre-wrap text-sm"
                  : "bg-slate-100 text-slate-800 rounded-2xl rounded-bl-sm px-4 py-2 max-w-[85%] whitespace-pre-wrap text-sm"
              }
            >
              {t.text}
            </div>
          </div>
        ))}
        {busy && <div className="text-sm text-slate-400">생각 중…</div>}

        {pending.length > 0 && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 space-y-2">
            <div className="font-semibold text-amber-800 text-sm">⚠️ 아래 작업을 실행할까요? 하지 않을 줄은 체크를 풀어 주세요.</div>
            <ul className="text-sm text-amber-900 space-y-1">
              {pending.map((p) => (
                <li key={p.id}>
                  <label className="flex items-start gap-2 cursor-pointer min-h-[32px]">
                    <input type="checkbox" className="mt-1" checked={!skipped.has(p.id)} onChange={() => toggle(p.id)} disabled={busy} />
                    <span className={skipped.has(p.id) ? "line-through text-amber-700/60" : ""}>{p.description}</span>
                  </label>
                </li>
              ))}
            </ul>
            {pending.some((p) => /전출/.test(p.description)) && (
              <p className="text-xs text-amber-800">전출한 학생은 학생 관리의 「전출 학생 되살리기」로 한 명씩 되돌릴 수 있습니다. 여러 명을 한꺼번에 되돌리지는 못하니 줄마다 확인해 주세요.</p>
            )}
            <div className="flex gap-2">
              <button className="btn-primary flex-1" onClick={execute} disabled={busy || chosen.length === 0}>
                {chosen.length === 0 ? "고른 작업 없음" : `실행 (${chosen.length}건${skipped.size ? ` · ${skipped.size}건 뺌` : ""})`}
              </button>
              <button className="btn-secondary flex-1" onClick={cancel} disabled={busy}>
                취소
              </button>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="flex gap-2">
        <input
          className="input flex-1"
          placeholder="명령을 입력하세요 (예: 해강중 미배송 몇 명?)"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.nativeEvent.isComposing) send();
          }}
          disabled={busy}
        />
        <button className="btn-primary" onClick={() => send()} disabled={busy || !input.trim()}>
          전송
        </button>
      </div>
    </div>
  );
}
